import React from 'react';

// SVG illustration for About page - showing international collaboration and IT expertise
export const AboutVisual = () => {
  return (
    <svg viewBox="0 0 800 800" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <defs>
        <linearGradient id="aboutGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0066CC" stopOpacity="0.9" />
          <stop offset="50%" stopColor="#2B3A4D" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#0066CC" stopOpacity="0.5" />
        </linearGradient>
        <linearGradient id="aboutGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#2B3A4D" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#0066CC" stopOpacity="0.6" />
        </linearGradient>
        <radialGradient id="aboutRadial" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#0066CC" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#2B3A4D" stopOpacity="0.1" />
        </radialGradient>
      </defs>
      
      {/* Background radial gradient */}
      <rect width="100%" height="100%" fill="url(#aboutRadial)" />
      
      {/* Central hub - representing HITECH GROUP with subtle pulse */}
      <circle cx="400" cy="400" r="80" fill="url(#aboutGrad1)" opacity="0.9">
        <animate attributeName="opacity" values="0.9;0.95;0.9" dur="4s" repeatCount="indefinite" />
      </circle>
      <circle cx="400" cy="400" r="60" fill="#0066CC" opacity="0.7">
        <animate attributeName="opacity" values="0.7;0.8;0.7" dur="3s" repeatCount="indefinite" />
      </circle>
      <circle cx="400" cy="400" r="40" fill="#2B3A4D" opacity="0.8" />
      
      {/* Central icon - representing IT expertise */}
      <g transform="translate(400, 400)">
        {/* Server/Network icon */}
        <rect x="-25" y="-15" width="50" height="30" rx="4" fill="#FFFFFF" opacity="0.9" />
        <rect x="-20" y="-10" width="40" height="4" rx="1" fill="#0066CC" />
        <rect x="-20" y="-2" width="40" height="4" rx="1" fill="#0066CC" />
        <rect x="-20" y="6" width="40" height="4" rx="1" fill="#0066CC" />
        <circle cx="-15" cy="0" r="3" fill="#0066CC">
          <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="15" cy="0" r="3" fill="#0066CC">
          <animate attributeName="opacity" values="1;0.5;1" dur="2s" begin="1s" repeatCount="indefinite" />
        </circle>
      </g>
      
      {/* International nodes - representing Global Services s.r.o. and international capabilities */}
      {[
        { angle: 0, label: 'Global' },
        { angle: 72, label: 'Local' },
        { angle: 144, label: 'Expertise' },
        { angle: 216, label: 'Technology' },
        { angle: 288, label: 'Partnership' },
      ].map((node, idx) => {
        const angleRad = (node.angle * Math.PI) / 180;
        const radius = 250;
        const cx = 400 + radius * Math.cos(angleRad);
        const cy = 400 + radius * Math.sin(angleRad);
        
        return (
          <g key={idx}>
            {/* Connection line with animated pulse */}
            <line
              x1="400"
              y1="400"
              x2={cx}
              y2={cy}
              stroke="#0066CC"
              strokeWidth="2"
              opacity="0.4"
              strokeDasharray="5,5"
            >
              <animate
                attributeName="opacity"
                values="0.4;0.6;0.4"
                dur="3s"
                begin={`${idx * 0.6}s`}
                repeatCount="indefinite"
              />
            </line>
            
            {/* Node circle with gentle pulse */}
            <circle cx={cx} cy={cy} r="35" fill="url(#aboutGrad2)" opacity="0.8">
              <animate
                attributeName="opacity"
                values="0.8;0.9;0.8"
                dur="4s"
                begin={`${idx * 0.8}s`}
                repeatCount="indefinite"
              />
            </circle>
            <circle cx={cx} cy={cy} r="25" fill="#0066CC" opacity="0.6">
              <animate
                attributeName="opacity"
                values="0.6;0.75;0.6"
                dur="3s"
                begin={`${idx * 0.6}s`}
                repeatCount="indefinite"
              />
            </circle>
            <circle cx={cx} cy={cy} r="12" fill="#FFFFFF" opacity="0.9">
              <animate
                attributeName="opacity"
                values="0.9;1;0.9"
                dur="2s"
                begin={`${idx * 0.4}s`}
                repeatCount="indefinite"
              />
            </circle>
            
            {/* Small decorative elements around nodes with gentle pulse */}
            <circle cx={cx + 20} cy={cy - 20} r="4" fill="#0066CC" opacity="0.5">
              <animate
                attributeName="opacity"
                values="0.5;0.7;0.5"
                dur="3s"
                begin={`${idx * 0.5}s`}
                repeatCount="indefinite"
              />
            </circle>
            <circle cx={cx - 20} cy={cy + 20} r="4" fill="#0066CC" opacity="0.5">
              <animate
                attributeName="opacity"
                values="0.5;0.7;0.5"
                dur="3s"
                begin={`${idx * 0.5 + 1.5}s`}
                repeatCount="indefinite"
              />
            </circle>
          </g>
        );
      })}
      
      {/* Data flow lines between nodes with subtle animation */}
      {[0, 1, 2, 3, 4].map((i) => {
        const angle1 = (i * 72 * Math.PI) / 180;
        const angle2 = ((i + 1) * 72 * Math.PI) / 180;
        const radius = 250;
        const x1 = 400 + radius * Math.cos(angle1);
        const y1 = 400 + radius * Math.sin(angle1);
        const x2 = 400 + radius * Math.cos(angle2);
        const y2 = 400 + radius * Math.sin(angle2);
        
        return (
          <path
            key={`flow-${i}`}
            d={`M ${x1} ${y1} Q ${400} ${400} ${x2} ${y2}`}
            stroke="#0066CC"
            strokeWidth="1.5"
            fill="none"
            opacity="0.2"
            strokeDasharray="3,3"
          >
            <animate
              attributeName="opacity"
              values="0.2;0.35;0.2"
              dur="5s"
              begin={`${i * 1}s`}
              repeatCount="indefinite"
            />
          </path>
        );
      })}
      
      {/* Floating particles representing team collaboration - gentle float */}
      {[
        { x: 150, y: 200, delay: 0 },
        { x: 650, y: 200, delay: 0.5 },
        { x: 150, y: 600, delay: 1 },
        { x: 650, y: 600, delay: 1.5 },
        { x: 300, y: 150, delay: 0.3 },
        { x: 500, y: 150, delay: 0.8 },
        { x: 300, y: 650, delay: 1.3 },
        { x: 500, y: 650, delay: 1.8 },
      ].map((particle, idx) => (
        <circle
          key={idx}
          cx={particle.x}
          cy={particle.y}
          r="6"
          fill="#0066CC"
          opacity="0.4"
        >
          <animate
            attributeName="opacity"
            values="0.4;0.6;0.4"
            dur="4s"
            begin={`${particle.delay}s`}
            repeatCount="indefinite"
          />
          <animateTransform
            attributeName="transform"
            type="translate"
            values={`0,0; 0,-8; 0,0`}
            dur="4s"
            begin={`${particle.delay}s`}
            repeatCount="indefinite"
          />
        </circle>
      ))}
      
      {/* Grid pattern overlay for tech aesthetic */}
      <defs>
        <pattern id="aboutGrid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#0066CC" strokeWidth="0.5" opacity="0.2" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#aboutGrid)" opacity="0.3" />
      
      {/* Decorative corner elements */}
      <rect x="50" y="50" width="60" height="2" fill="#0066CC" opacity="0.6" />
      <rect x="50" y="50" width="2" height="60" fill="#0066CC" opacity="0.6" />
      <rect x="690" y="50" width="60" height="2" fill="#0066CC" opacity="0.6" />
      <rect x="748" y="50" width="2" height="60" fill="#0066CC" opacity="0.6" />
      <rect x="50" y="690" width="60" height="2" fill="#0066CC" opacity="0.6" />
      <rect x="50" y="748" width="2" height="60" fill="#0066CC" opacity="0.6" />
      <rect x="690" y="748" width="60" height="2" fill="#0066CC" opacity="0.6" />
      <rect x="748" y="690" width="2" height="60" fill="#0066CC" opacity="0.6" />
    </svg>
  );
};

export default AboutVisual;
